import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UserModel } from 'app/models/user.model';
import { FileModuleModel } from 'app/models/file_module.model';
import { FileModuleService } from 'app/services/file_module.service';
import {
    UserDocumentSignatureListItem,
    UserDocumentSignatureService,
} from 'app/services/user-document-signature.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ApiService } from 'app/shared/apis/api.service';
import { S3Service } from 'app/shared/services/s3.service';
import { ImageViewerService } from 'app/shared/services/image-viewer.service';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-user-document-signatures',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
    ],
    templateUrl: './user-document-signatures.component.html',
})
export class UserDocumentSignaturesComponent implements OnChanges {
    @Input() userId: number;
    @Input() loggedUser: UserModel;

    items: UserDocumentSignatureListItem[] = [];
    pendingFiles: File[] = [];
    toDelete: FileModuleModel[] = [];
    loading = false;
    saving = false;
    actionLoadingId: number | null = null;
    batchLoading = false;
    selectedIds = new Set<number>();

    constructor(
        private signatureService: UserDocumentSignatureService,
        private fileModuleService: FileModuleService,
        private toast: ToastService,
        private api: ApiService,
        private s3: S3Service,
        private imageViewerService: ImageViewerService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userId'] && this.userId) {
            this.pendingFiles = [];
            this.toDelete = [];
            this.selectedIds.clear();
            this.loadItems();
        }
    }

    get hasChanges(): boolean {
        return this.pendingFiles.length > 0 || this.toDelete.length > 0;
    }

    get pendingUnsignedItems(): UserDocumentSignatureListItem[] {
        return this.items.filter((i) => i.signature?.status !== 'signed');
    }

    get selectedPendingCount(): number {
        return this.pendingUnsignedItems.filter((i) =>
            this.selectedIds.has(i.file_module?.file_module_id),
        ).length;
    }

    get allPendingSelected(): boolean {
        const pending = this.pendingUnsignedItems;
        return pending.length > 0 && pending.every((i) => this.selectedIds.has(i.file_module?.file_module_id));
    }

    async loadItems(): Promise<void> {
        if (!this.userId) return;
        this.loading = true;
        try {
            const resp = await firstValueFrom(this.signatureService.listByUser(this.userId));
            this.items = resp?.items || [];
            // Limpar seleção de ids que não existem mais
            const valid = new Set(this.items.map((i) => i.file_module?.file_module_id).filter(Boolean));
            this.selectedIds = new Set([...this.selectedIds].filter((id) => valid.has(id)));
        } catch (error) {
            console.error(error);
            this.items = [];
            this.toast.error('Erro ao carregar acordos para assinatura.');
        } finally {
            this.loading = false;
        }
    }

    onSelectFiles(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files) return;
        this.pendingFiles.push(...Array.from(input.files));
        input.value = '';
        this.toast.info('Arquivo(s) adicionados. Clique em Salvar para enviar.');
    }

    removePending(index: number): void {
        this.pendingFiles.splice(index, 1);
    }

    removeItem(item: UserDocumentSignatureListItem): void {
        if (!item.file_module) return;
        this.toDelete.push(new FileModuleModel(item.file_module as any));
        this.selectedIds.delete(item.file_module.file_module_id);
        this.items = this.items.filter((i) => i.file_module?.file_module_id !== item.file_module.file_module_id);
        this.toast.info('Arquivo marcado para exclusão. Clique em Salvar para confirmar.');
    }

    toggleSelect(item: UserDocumentSignatureListItem, checked: boolean): void {
        const id = item.file_module?.file_module_id;
        if (!id || item.signature?.status === 'signed') return;
        if (checked) this.selectedIds.add(id);
        else this.selectedIds.delete(id);
    }

    isSelected(item: UserDocumentSignatureListItem): boolean {
        return this.selectedIds.has(item.file_module?.file_module_id);
    }

    toggleSelectAllPending(checked: boolean): void {
        if (checked) {
            for (const item of this.pendingUnsignedItems) {
                const id = item.file_module?.file_module_id;
                if (id) this.selectedIds.add(id);
            }
        } else {
            for (const item of this.pendingUnsignedItems) {
                this.selectedIds.delete(item.file_module?.file_module_id);
            }
        }
    }

    private getSelectedOrPendingIds(): number[] {
        const selected = this.pendingUnsignedItems
            .map((i) => i.file_module?.file_module_id)
            .filter((id): id is number => !!id && this.selectedIds.has(id));
        if (selected.length) return selected;
        return this.pendingUnsignedItems
            .map((i) => i.file_module?.file_module_id)
            .filter((id): id is number => !!id);
    }

    async save(): Promise<void> {
        if (!this.userId) {
            this.toast.error('Salve o cadastro antes de enviar acordos.');
            return;
        }
        if (!this.hasChanges) {
            this.toast.info('Nenhuma alteração para salvar');
            return;
        }

        this.saving = true;
        try {
            for (const file of this.toDelete) {
                try {
                    await this.s3.deleteFile(file.path);
                } catch (error) {
                    console.warn('Falha ao remover do S3:', error);
                }
                await this.fileModuleService.delete(file).pipe(take(1)).toPromise();
            }
            this.toDelete = [];

            for (const file of this.pendingFiles) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('path', 'modules/user/attachments');
                formData.append('name', file.name);
                const resp = await this.api
                    .postSub<{ url: string }, FormData>('storage/upload', formData)
                    .pipe(take(1))
                    .toPromise();
                await this.fileModuleService
                    .create({
                        path: resp.url,
                        type_file: 'AcordoMensal',
                        module: 'USER',
                        entity_id: this.userId,
                        user_id: this.loggedUser?.user_id,
                        name: file.name,
                    } as any)
                    .pipe(take(1))
                    .toPromise();
            }
            this.pendingFiles = [];
            this.toast.success('Acordos atualizados com sucesso');
            await this.loadItems();
        } catch (error) {
            console.error(error);
            this.toast.error('Erro ao salvar acordos. Tente novamente.');
        } finally {
            this.saving = false;
        }
    }

    statusLabel(item: UserDocumentSignatureListItem): string {
        const status = item.signature?.status;
        if (!status) return 'Sem link';
        if (status === 'signed') return 'Assinado';
        if (status === 'expired') return 'Expirado';
        if (status === 'pending') return 'Pendente';
        return status;
    }

    statusClass(item: UserDocumentSignatureListItem): string {
        const status = item.signature?.status;
        if (status === 'signed') return 'text-green-700 bg-green-50';
        if (status === 'expired') return 'text-red-700 bg-red-50';
        if (status === 'pending') return 'text-amber-700 bg-amber-50';
        return 'text-gray-600 bg-gray-100';
    }

    getFileName(item: UserDocumentSignatureListItem): string {
        return item.file_module?.name || this.fileNameFromPath(item.file_module?.path) || 'Acordo';
    }

    private fileNameFromPath(path: string): string {
        if (!path) return '';
        try {
            const decoded = decodeURIComponent(path);
            return decoded.split('/').pop() || decoded;
        } catch {
            return path.split('/').pop() || path;
        }
    }

    openFile(item: UserDocumentSignatureListItem): void {
        const path = item.file_module?.path;
        if (!path) {
            this.toast.warning('Arquivo não disponível');
            return;
        }
        const name = this.getFileName(item).toLowerCase();
        if (/\.(jpg|jpeg|png|gif|webp)$/.test(name)) {
            this.imageViewerService.openLightbox(path, [path], 0);
        } else {
            this.imageViewerService.openInNewTab(path);
        }
    }

    async downloadOriginal(item: UserDocumentSignatureListItem): Promise<void> {
        const path = item.file_module?.path;
        if (!path) {
            this.toast.warning('Arquivo não disponível');
            return;
        }
        const name = this.getFileName(item).toLowerCase();
        const isInlineViewable = /\.(jpg|jpeg|png|gif|webp|svg|bmp|pdf)$/.test(name);
        if (isInlineViewable) {
            await this.imageViewerService.openInNewTab(path);
            return;
        }
        try {
            const normalizedKey = this.normalizeS3Key(path);
            const response = await this.api
                .postSub<{ url: string }, { urlOrKey: string }>('storage/download-url', { urlOrKey: normalizedKey })
                .pipe(take(1))
                .toPromise();
            if (!response?.url) throw new Error('Failed to get presigned URL.');

            const fetchResponse = await fetch(response.url, { method: 'GET', credentials: 'omit' });
            if (!fetchResponse.ok) throw new Error(`HTTP error! status: ${fetchResponse.status}`);
            const blob = await fetchResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = this.getFileName(item);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 200);
        } catch (error) {
            console.error(error);
            this.toast.error('Erro ao baixar arquivo');
        }
    }

    private async copyUrlToClipboard(url: string): Promise<void> {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = url;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }
    }

    async copyLink(item: UserDocumentSignatureListItem): Promise<void> {
        const fileId = item.file_module?.file_module_id;
        if (!fileId) return;
        this.actionLoadingId = fileId;
        try {
            const result = await firstValueFrom(this.signatureService.createLink(fileId));
            await this.copyUrlToClipboard(result.signature_url);
            this.toast.success('Link copiado para a área de transferência');
            await this.loadItems();
        } catch (error: any) {
            console.error(error);
            this.toast.error(error?.error?.message || 'Erro ao gerar link');
        } finally {
            this.actionLoadingId = null;
        }
    }

    async sendEmail(item: UserDocumentSignatureListItem): Promise<void> {
        const fileId = item.file_module?.file_module_id;
        if (!fileId) return;
        this.actionLoadingId = fileId;
        try {
            const result = await firstValueFrom(this.signatureService.sendEmail(fileId));
            this.toast.success(result.message || `Email enviado para ${result.email_sent}`);
            await this.loadItems();
        } catch (error: any) {
            console.error(error);
            this.toast.error(error?.error?.message || 'Erro ao enviar email');
        } finally {
            this.actionLoadingId = null;
        }
    }

    async copyLinkBatch(): Promise<void> {
        const ids = this.getSelectedOrPendingIds();
        if (!ids.length) {
            this.toast.info('Nenhum acordo pendente para gerar link');
            return;
        }
        this.batchLoading = true;
        try {
            const result = await firstValueFrom(this.signatureService.createLinkBatch(ids));
            await this.copyUrlToClipboard(result.signature_url);
            this.toast.success(
                ids.length > 1
                    ? `Link único para ${ids.length} arquivos copiado`
                    : 'Link copiado para a área de transferência',
            );
            this.selectedIds.clear();
            await this.loadItems();
        } catch (error: any) {
            console.error(error);
            this.toast.error(error?.error?.message || 'Erro ao gerar link do pacote');
        } finally {
            this.batchLoading = false;
        }
    }

    async sendEmailBatch(): Promise<void> {
        const ids = this.getSelectedOrPendingIds();
        if (!ids.length) {
            this.toast.info('Nenhum acordo pendente para enviar e-mail');
            return;
        }
        this.batchLoading = true;
        try {
            const result = await firstValueFrom(this.signatureService.sendEmailBatch(ids));
            this.toast.success(
                result.message ||
                    `Email com ${ids.length} arquivo(s) enviado para ${result.email_sent}`,
            );
            this.selectedIds.clear();
            await this.loadItems();
        } catch (error: any) {
            console.error(error);
            this.toast.error(error?.error?.message || 'Erro ao enviar e-mail do pacote');
        } finally {
            this.batchLoading = false;
        }
    }

    async downloadSigned(item: UserDocumentSignatureListItem): Promise<void> {
        const path = item.signature?.signed_pdf_path;
        if (!path) {
            this.toast.warning('PDF assinado ainda não disponível');
            return;
        }
        try {
            const normalizedKey = this.normalizeS3Key(path);
            const response = await this.api
                .postSub<{ url: string }, { urlOrKey: string }>('storage/download-url', { urlOrKey: normalizedKey })
                .pipe(take(1))
                .toPromise();
            if (!response?.url) throw new Error('Failed to get presigned URL.');
            window.open(response.url, '_blank');
        } catch (error) {
            console.error(error);
            this.toast.error('Erro ao baixar PDF assinado');
        }
    }

    private normalizeS3Key(url: string): string {
        if (!url) return '';
        const cdnPrefix = 'https://cdn.artsys.com/';
        if (url.startsWith(cdnPrefix)) return url.slice(cdnPrefix.length);
        const knownPrefixes = ['https://evobrides.s3.amazonaws.com/', 'https://art-sys.s3.amazonaws.com/'];
        for (const prefix of knownPrefixes) {
            if (url.startsWith(prefix)) return url.slice(prefix.length);
        }
        try {
            const parsed = new URL(url);
            if (parsed.hostname.endsWith('s3.amazonaws.com')) {
                return parsed.pathname.replace(/^\/+/, '');
            }
        } catch {
            // ignore
        }
        return url;
    }
}
